/**
 * Floating "Compare tray" — appears in the bottom-right corner while the
 * visitor queues 1-3 properties. Enabled once 2+ are queued.
 *
 * Mounted globally via PageShell so it works from anywhere the visitor
 * browses (properties list, single property page, home page carousel).
 */
import { Link, useLocation } from "react-router-dom";
import { GitCompare, X } from "lucide-react";
import { useCompareCart } from "@/hooks/useCompareCart";
import { resolveImageUrl } from "@/utils/imageUrl";

export default function CompareTray() {
  const location = useLocation();
  const { items, clear, remove, max } = useCompareCart();

  // Never render on /compare itself (would be redundant + confusing) or on
  // any admin route (public-only feature).
  const path = location.pathname || "";
  if (items.length === 0) return null;
  if (path.startsWith("/compare")) return null;
  if (path.startsWith("/admin") || path.startsWith("/property-admin")) return null;

  const ids = items.map((i) => i.property_id).join(",");
  const canGo = items.length >= 2;

  return (
    <div
      className="fixed bottom-24 right-4 z-40 max-w-xs w-full sm:w-80 bg-white border border-border shadow-xl rounded-lg p-3 space-y-2 animate-in fade-in slide-in-from-bottom-2"
      data-testid="compare-tray"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <GitCompare className="h-4 w-4 text-primary" />
          Compare {items.length} of {max}
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="compare-tray-clear"
        >
          Clear
        </button>
      </div>

      <ul className="space-y-1.5">
        {items.map((i) => {
          const p = i.property || {};
          const img = (p.gallery || [])[0]?.media_url || "";
          return (
            <li
              key={i.property_id}
              className="flex items-center gap-2 border border-border rounded px-1.5 py-1"
              data-testid={`compare-tray-item-${i.property_id}`}
            >
              <div
                className="h-8 w-10 bg-cover bg-center bg-muted rounded shrink-0"
                style={img ? { backgroundImage: `url(${resolveImageUrl(img)})` } : {}}
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{p.title || "Property"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.display_address || ""}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(i.property_id)}
                className="p-1 rounded hover:bg-muted text-muted-foreground"
                title="Remove"
                data-testid={`compare-tray-remove-${i.property_id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          );
        })}
      </ul>

      <Link
        to={canGo ? `/compare?ids=${ids}` : "#"}
        state={canGo ? { from: `${location.pathname}${location.search}` } : undefined}
        onClick={(e) => { if (!canGo) e.preventDefault(); }}
        className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
          canGo ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-muted text-muted-foreground cursor-not-allowed"
        }`}
        data-testid="compare-tray-open-btn"
      >
        <GitCompare className="h-3.5 w-3.5" />
        {canGo ? `Compare ${items.length}` : "Pick 1 more to compare"}
      </Link>
    </div>
  );
}
